import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, UploadCloud, Trash2, Plus, AlertTriangle, CheckCircle2, MessageCircle } from "lucide-react";
import { Logo, Eyebrow } from "@/components/Reveal";
import ThemeToggle from "@/components/ThemeToggle";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

const FIELDS = ["description", "brand", "size", "cores", "material", "armour", "quantity", "unit"];
const LABELS = { description: "Description", brand: "Brand", size: "Size", cores: "Cores", material: "Material", armour: "Armour", quantity: "Qty", unit: "Unit" };
const ALLOWED = ["xlsx", "xls", "csv", "pdf", "png", "jpg", "jpeg", "webp"];
const MAX_MB = 8;

const inputCls = "w-full bg-paper border border-line rounded-md px-3 py-2 text-xs text-ink focus:outline-none focus:border-copper";

export default function UploadBOQ() {
    const [job, setJob] = useState(null);
    const [items, setItems] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const [contact, setContact] = useState({ name: "", phone: "", email: "", company: "" });
    const fileRef = useRef(null);

    const pickFile = async (file) => {
        if (!file) return;
        const ext = file.name.split(".").pop().toLowerCase();
        if (!ALLOWED.includes(ext)) {
            toast.error("Unsupported file type", { description: "Upload XLSX, XLS, CSV, PDF or an image." });
            return;
        }
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error("File too large", { description: `${MAX_MB} MB maximum.` });
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post("/boq/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setJob(res.data);
            setItems(res.data.items || []);
            if (res.data.note) toast.info(res.data.note);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed — please retry.");
        } finally {
            setUploading(false);
        }
    };

    const setItem = (i, key, val) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
    const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
    const addItem = () =>
        setItems((prev) => [...prev, { description: "", brand: "", size: "", cores: "", material: "", armour: "", quantity: "", unit: "mtrs", matched_sku: "", confidence: "manual", uncertain: true }]);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.put(`/boq/${job.job_id}/items`, { items });
            const res = await api.post(`/boq/${job.job_id}/submit`, contact);
            setDone(res.data);
        } catch {
            toast.error("Could not submit — please retry or WhatsApp us the file.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="boq-page">
            <Seo
                title="Upload BOQ for Cable Quotation"
                description="Upload your bill of quantities (CSV, Excel, PDF or image) and Roxy Cables matches it to the catalogue and returns a quotation."
                path="/upload-boq"
            />
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-paper/85 border-b border-line">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo size={28} />
                        <span className="font-display font-bold tracking-[0.06em] text-lg text-ink hidden sm:block">Roxy Cables</span>
                        <span className="font-mono text-[10px] tracking-[0.25em] text-copper uppercase ml-2">Upload BOQ</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <ThemeToggle />
                        <Link to="/" data-testid="boq-back-link" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-copper transition-colors duration-300">
                            <ArrowLeft size={14} /> Site
                        </Link>
                    </div>
                </div>
            </header>

            <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-14">
                <Eyebrow className="mb-4">BOQ → Quotation</Eyebrow>
                <h1 className="font-display font-bold tracking-tight leading-[1.05] text-4xl md:text-5xl text-ink mb-4">
                    Drop your BOQ. <em className="text-copper">Get a quote.</em>
                </h1>
                <p className="text-mist max-w-xl leading-relaxed mb-10">
                    Upload your bill of quantities — XLSX, XLS, CSV, PDF or a clear photo. We extract the line
                    items, you review and edit them, then it lands on our desk as a quotation enquiry.
                </p>

                {!job && (
                    <button
                        data-testid="boq-dropzone"
                        onClick={() => fileRef.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
                        className="w-full rounded-md border-2 border-dashed border-line bg-white hover:border-copper transition-colors duration-300 p-14 flex flex-col items-center justify-center text-center"
                    >
                        <UploadCloud size={36} className="text-copper mb-4" />
                        <p className="font-display font-semibold text-xl text-ink mb-1">
                            {uploading ? "Uploading & extracting…" : "Click or drop your BOQ file"}
                        </p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                            XLSX · XLS · CSV · PDF · PNG · JPG — max 8 MB
                        </p>
                        <input
                            ref={fileRef}
                            data-testid="boq-upload-input"
                            type="file"
                            accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp"
                            className="hidden"
                            onChange={(e) => pickFile(e.target.files?.[0])}
                        />
                    </button>
                )}

                {job && !done && (
                    <div>
                        {job.note && (
                            <div className="rounded-md border border-amber-300 bg-amber-50 px-5 py-4 mb-6 flex items-start gap-3" data-testid="boq-note">
                                <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-900">{job.note}</p>
                            </div>
                        )}
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-3">
                            {job.original_filename} · review every row — AI/parsed extraction is a starting point, not gospel
                        </p>

                        <div className="rounded-md border border-line bg-white overflow-x-auto mb-6" data-testid="boq-review-table">
                            <table className="w-full min-w-[900px] text-sm">
                                <thead>
                                    <tr className="border-b border-line bg-paper2">
                                        {FIELDS.map((f) => (
                                            <th key={f} className="text-left font-mono text-[9px] uppercase tracking-[0.15em] text-dim px-3 py-3">{LABELS[f]}</th>
                                        ))}
                                        <th className="text-left font-mono text-[9px] uppercase tracking-[0.15em] text-dim px-3 py-3">Catalogue Match</th>
                                        <th className="px-3 py-3" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((it, i) => (
                                        <tr key={i} data-testid={`boq-item-row-${i}`} className={`border-b border-line last:border-b-0 ${it.uncertain && !it.matched_sku ? "bg-amber-50/60" : ""}`}>
                                            {FIELDS.map((f) => (
                                                <td key={f} className="px-2 py-2">
                                                    <input
                                                        data-testid={`boq-item-${i}-${f}`}
                                                        value={it[f] || ""}
                                                        onChange={(e) => setItem(i, f, e.target.value)}
                                                        className={inputCls}
                                                        placeholder="—"
                                                    />
                                                </td>
                                            ))}
                                            <td className="px-2 py-2 whitespace-nowrap">
                                                {it.matched_sku ? (
                                                    <span data-testid={`boq-item-${i}-match`} className="font-mono text-[10px] tracking-[0.08em] bg-copper-light text-copper border border-copper/30 px-2 py-1 rounded-full">
                                                        {it.matched_sku}
                                                    </span>
                                                ) : (
                                                    <span data-testid={`boq-item-${i}-match`} className="font-mono text-[10px] tracking-[0.08em] text-amber-700">
                                                        No match — will confirm manually
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-2 py-2 text-center">
                                                <button data-testid={`boq-item-delete-${i}`} onClick={() => removeItem(i)} className="text-dim hover:text-red-600 transition-colors" aria-label="Delete row">
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <button
                            data-testid="boq-add-row"
                            onClick={addItem}
                            className="mb-8 rounded-full border border-line text-mist font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-2.5 hover:border-copper hover:text-copper transition-colors duration-300 flex items-center gap-2"
                        >
                            <Plus size={13} /> Add row
                        </button>

                        <form onSubmit={submit} data-testid="boq-submit-form" className="rounded-md border border-line bg-white p-6 md:p-8">
                            <p className="font-display font-semibold text-xl text-ink mb-5">Where do we send the quotation?</p>
                            <div className="grid sm:grid-cols-2 gap-4 mb-4">
                                <input data-testid="boq-input-name" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Name *" className={inputCls + " !py-3 !text-sm"} />
                                <input data-testid="boq-input-phone" required type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Phone *" className={inputCls + " !py-3 !text-sm"} />
                                <input data-testid="boq-input-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email" className={inputCls + " !py-3 !text-sm"} />
                                <input data-testid="boq-input-company" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} placeholder="Company" className={inputCls + " !py-3 !text-sm"} />
                            </div>
                            <p className="font-mono text-[10px] text-dim mb-5">By submitting you agree to be contacted about this BOQ. Nothing is priced automatically — our desk sends the quotation.</p>
                            <button
                                data-testid="boq-submit-btn"
                                disabled={submitting}
                                className="w-full rounded-full bg-copper text-white font-mono uppercase tracking-[0.18em] text-[11px] py-4 hover:bg-copper-bright transition-colors duration-300 disabled:opacity-50"
                            >
                                {submitting ? "Submitting…" : "Submit for Quotation"}
                            </button>
                        </form>
                    </div>
                )}

                {done && (
                    <div className="rounded-md border border-line bg-white p-10 text-center" data-testid="boq-success">
                        <CheckCircle2 size={36} className="text-emerald-600 mx-auto mb-4" />
                        <h2 className="font-display font-bold text-3xl text-ink mb-2">BOQ received.</h2>
                        <p className="text-mist text-sm mb-1">Enquiry reference</p>
                        <p className="font-mono text-copper text-sm mb-6">{done.enquiry_id}</p>
                        <p className="text-mist text-sm mb-8 max-w-md mx-auto">Our desk will review the file and your line items, then return a quotation within one working day.</p>
                        <a
                            href={"https://wa.me/919008177176?text=" + encodeURIComponent(`Hello Roxy Cables, I just submitted BOQ ${done.enquiry_id}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-mono uppercase tracking-[0.15em] text-[11px] px-6 py-3 hover:opacity-90 transition-opacity"
                        >
                            <MessageCircle size={14} /> Track on WhatsApp
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
